import { useMutation, useQueryClient, type QueryKey } from '@tanstack/react-query'
import type { StrategyDetail, StrategyView } from '@/lib/api-types'
import { errorMessage, useTRPC } from '@/lib/trpc'
import { useToast } from '@/components/toast'
import { NOTICE_MS, strategyNotice } from '@/components/strategy-notice'

/** the server's order for the list (strategy.service STRATEGY_LIST_ORDER): live first, then paused, failed, completed, stopped; newest first within */
const LIST_ORDER: Record<string, number> = { scheduled: 0, active: 0, paused: 1, failed: 2, completed: 3, cancelled: 4 }
const madeAt = (row: { createdAt: string | null }) => (row.createdAt ? Date.parse(row.createdAt) : 0)

/** the list in the order the server keeps it, so a row changed here lands where the server's answer will put it */
export function sortStrategies<T extends { status: string; createdAt: string | null }>(rows: readonly T[]): T[] {
  return [...rows].sort((a, b) => (LIST_ORDER[a.status] ?? 9) - (LIST_ORDER[b.status] ?? 9) || madeAt(b) - madeAt(a))
}

export type StrategyAction = 'pause' | 'resume' | 'cancel'
type Status = StrategyView['status']
const NEXT_STATUS: Record<StrategyAction, Status> = { pause: 'paused' as Status, resume: 'active' as Status, cancel: 'cancelled' as Status }
/** the quick notice's word for each */
const DID: Record<StrategyAction, string> = { pause: 'paused', resume: 'resumed', cancel: 'stopped' }

type Snapshot = { list: [QueryKey, StrategyView[] | undefined][]; detail: StrategyDetail | undefined }
type Vars = { id: string }

/**
 * Pause, resume and stop with the answer shown at once (John, Nov 5 2026:
 * "I don't want to wait 3 seconds for the backend"): the row changes status
 * and place the moment the button is pressed, in the server's own order, and
 * the detail dialog with it. The server's reply then confirms it; a refusal
 * puts everything back as it was and `onError` says why. Every reader of
 * the list and the detail is told to read again once the server has
 * answered, so what shows is what the server holds.
 */
export function useStrategyActions(onError: (action: StrategyAction, message: string) => void) {
  const trpc = useTRPC()
  const queryClient = useQueryClient()
  const toast = useToast()
  const listKey = trpc.strategy.list.queryKey()
  const detailKey = (id: string) => trpc.strategy.get.queryKey({ id })

  const show = async (id: string, action: StrategyAction): Promise<Snapshot> => {
    await Promise.all([queryClient.cancelQueries({ queryKey: listKey }), queryClient.cancelQueries({ queryKey: detailKey(id) })])
    const before: Snapshot = { list: queryClient.getQueriesData<StrategyView[]>({ queryKey: listKey }), detail: queryClient.getQueryData<StrategyDetail>(detailKey(id)) }
    const status = NEXT_STATUS[action]
    const change = action === 'cancel' ? { status, nextRunAt: null } : { status }
    queryClient.setQueriesData<StrategyView[]>({ queryKey: listKey }, (rows) => (rows ? sortStrategies(rows.map((row) => (row.id === id ? { ...row, ...change } : row))) : rows))
    queryClient.setQueryData<StrategyDetail>(detailKey(id), (detail) => (detail && detail.id === id ? { ...detail, ...change } : detail))
    // the quick notice at the bottom (John, Nov 5 2026), gone in a few seconds; a refusal's own toast follows it if it comes
    const row = before.list.flatMap(([, rows]) => rows ?? []).find((r) => r.id === id) ?? before.detail
    if (row) {
      const notice = strategyNotice(row, DID[action])
      toast('success', notice.title, undefined, { icon: notice.icon, ms: NOTICE_MS })
    }
    return before
  }
  const putBack = (id: string, before: Snapshot) => {
    for (const [key, rows] of before.list) queryClient.setQueryData(key, rows)
    if (before.detail !== undefined) queryClient.setQueryData<StrategyDetail>(detailKey(id), before.detail)
  }
  const confirm = (id: string) => {
    void queryClient.invalidateQueries({ queryKey: listKey })
    void queryClient.invalidateQueries({ queryKey: detailKey(id) })
  }
  const handlers = (action: StrategyAction) => ({
    onMutate: ({ id }: Vars) => show(id, action),
    onError: (err: unknown, { id }: Vars, before: Snapshot | undefined) => {
      if (before) putBack(id, before)
      onError(action, errorMessage(err))
    },
    onSettled: (_data: unknown, _err: unknown, { id }: Vars) => confirm(id),
  })
  const pause = useMutation(trpc.strategy.pause.mutationOptions(handlers('pause')))
  const resume = useMutation(trpc.strategy.resume.mutationOptions(handlers('resume')))
  const cancel = useMutation(trpc.strategy.cancel.mutationOptions(handlers('cancel')))
  return { pause, resume, cancel }
}
